import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import { authAxios } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Play, Pause, SkipForward, RotateCcw, Users, Trophy } from 'lucide-react';

export default function DraftReplay() {
  const { seasonId } = useParams();
  const { user, API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [replayData, setReplayData] = useState(null);
  const [visiblePicks, setVisiblePicks] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per pick

  const loadReplayData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(`${API}/seasons/${seasonId}/draft/replay`);
      setReplayData(response.data);
    } catch (error) {
      console.error('Failed to load draft replay:', error);
      toast.error('Failed to load draft replay');
    } finally {
      setLoading(false);
    }
  }, [API, seasonId]);

  useEffect(() => {
    loadReplayData();
  }, [loadReplayData]);

  // Auto-play effect
  useEffect(() => {
    if (!isPlaying || !replayData) return;
    
    if (visiblePicks >= replayData.total_picks) {
      setIsPlaying(false);
      return;
    }
    
    const timer = setTimeout(() => {
      setVisiblePicks(prev => prev + 1);
    }, playbackSpeed);
    
    return () => clearTimeout(timer);
  }, [isPlaying, visiblePicks, replayData, playbackSpeed]);

  const handlePlay = () => {
    if (visiblePicks >= replayData?.total_picks) {
      setVisiblePicks(0);
    }
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleSkipToEnd = () => {
    setIsPlaying(false);
    setVisiblePicks(replayData?.total_picks || 0);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setVisiblePicks(0);
  };

  const getPlayerRoster = (playerId) => {
    if (!replayData) return [];
    // Only show picks that have been revealed (pick_number <= visiblePicks)
    return replayData.picks
      .filter(p => p.player_id === playerId && p.pick_number <= visiblePicks)
      .map(p => ({
        name: p.contestant_name,
        photo: p.contestant_photo,
        pickNumber: p.pick_number,
        round: p.round_number
      }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading draft replay...</p>
        </div>
      </Layout>
    );
  }

  if (!replayData || replayData.total_picks === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto container-mobile">
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-slate-600">No draft data available yet.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const currentPick = replayData.picks[visiblePicks - 1];
  const isComplete = visiblePicks >= replayData.total_picks;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto container-mobile">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Draft Replay
          </h1>
          <p className="text-responsive text-slate-600">
            {replayData.draft_mode === 'auto' ? 'Auto-Draft Results' : 'Live Draft Results'}
            {' • '}{replayData.draft_order_type === 'snake' ? 'Snake Order' : replayData.draft_order_type}
          </p>
        </div>

        {/* Playback Controls */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                {!isPlaying ? (
                  <Button 
                    onClick={handlePlay}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={isComplete}
                  >
                    <Play className="w-4 h-4 mr-1" />
                    {visiblePicks === 0 ? 'Play' : 'Resume'}
                  </Button>
                ) : (
                  <Button onClick={handlePause} variant="outline">
                    <Pause className="w-4 h-4 mr-1" />
                    Pause
                  </Button>
                )}
                <Button onClick={handleSkipToEnd} variant="outline" disabled={isComplete}>
                  <SkipForward className="w-4 h-4 mr-1" />
                  Skip to End
                </Button>
                <Button onClick={handleReset} variant="ghost" disabled={visiblePicks === 0}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-600">
                  Speed:
                  <select 
                    value={playbackSpeed}
                    onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                    className="ml-2 border rounded px-2 py-1"
                  >
                    <option value={2000}>Slow</option>
                    <option value={1000}>Normal</option>
                    <option value={500}>Fast</option>
                    <option value={200}>Very Fast</option>
                  </select>
                </div>
                <div className="text-sm font-medium">
                  Pick {visiblePicks} / {replayData.total_picks}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 bg-slate-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-amber-500 h-full transition-all duration-300"
                style={{ width: `${(visiblePicks / replayData.total_picks) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Current Pick Highlight */}
        {currentPick && !isComplete && (
          <Card className="mb-6 border-amber-400 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-xs text-amber-600 uppercase font-semibold">Pick #{currentPick.pick_number}</div>
                  <div className="text-xs text-amber-500">Round {currentPick.round_number}</div>
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: currentPick.player_color }}
                  />
                  <span className="font-semibold text-slate-800">{currentPick.player_name}</span>
                  <span className="text-slate-500">selects</span>
                  {currentPick.contestant_photo ? (
                    <img 
                      src={currentPick.contestant_photo} 
                      alt={currentPick.contestant_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200" />
                  )}
                  <span className="font-semibold text-amber-700">{currentPick.contestant_name}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completion Message */}
        {isComplete && (
          <Card className="mb-6 border-green-400 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 text-green-800">
                <Trophy className="w-6 h-6" />
                <div>
                  <p className="font-semibold">Draft Complete!</p>
                  <p className="text-sm">All {replayData.total_picks} picks have been made.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Player Rosters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {replayData.players.map(player => {
            const roster = getPlayerRoster(player.id);
            const isCurrentPlayer = currentPick?.player_id === player.id && !isComplete;
            
            return (
              <Card 
                key={player.id} 
                className={`transition-all ${isCurrentPlayer ? 'ring-2 ring-amber-400' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: player.color }}
                    />
                    <h3 className="font-semibold text-slate-800">{player.name}</h3>
                    {player.id === user?.id && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">You</span>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {roster.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">No picks yet</p>
                    ) : (
                      roster.map((pick, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center gap-2 p-2 rounded-lg bg-slate-50 ${
                            pick.pickNumber === visiblePicks ? 'ring-2 ring-amber-400 bg-amber-50' : ''
                          }`}
                        >
                          <span className="text-xs text-slate-400 w-6">#{pick.pickNumber}</span>
                          {pick.photo ? (
                            <img 
                              src={pick.photo} 
                              alt={pick.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <span className="text-xs text-slate-500">{pick.name?.charAt(0)}</span>
                            </div>
                          )}
                          <span className="text-sm font-medium truncate">{pick.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Full Pick Log (collapsible) */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <details>
              <summary className="font-semibold cursor-pointer text-slate-700 hover:text-slate-900">
                Full Pick Log ({visiblePicks} picks shown)
              </summary>
              <div className="mt-4 max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Pick</th>
                      <th className="px-2 py-1 text-left">Round</th>
                      <th className="px-2 py-1 text-left">Player</th>
                      <th className="px-2 py-1 text-left">Contestant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {replayData.picks.slice(0, visiblePicks).map((pick, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">{pick.pick_number}</td>
                        <td className="px-2 py-1">{pick.round_number}</td>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: pick.player_color }}
                            />
                            {pick.player_name}
                          </div>
                        </td>
                        <td className="px-2 py-1">{pick.contestant_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
